import { UserRole } from '../types/UserRole';
/**
 * 出品区分
 */
export const ListingCategoryConst = 
{
  /**
   * 相対
   */
  PRIVATE: 1,
  /**
   * 通常入札
   */
  BIDDING: 2,
  /**
   * 入札会
   */
  SALE: 3,
} as const;
export type  ListingCategoryType = typeof ListingCategoryConst[keyof typeof ListingCategoryConst];

// 県内・県外の値
export const PrefTypeConst = {
  /**
   * 指定なし。入札会は県内県外の区別を持たず 0 で運用する
   * （BiddingSchedules も PrefectureType=0 で登録する）
   */
  NONE: 0,
  INTERNAL: 1,
  EXTERNAL: 2,
} as const;
export type PrefectureType = typeof PrefTypeConst[keyof typeof PrefTypeConst];

// ラベル表示用（入札会は県区分を持たないためラベルなし）
export const PrefCategoryLabel = {
  [PrefTypeConst.NONE]: "",
  [PrefTypeConst.INTERNAL]: "県内",
  [PrefTypeConst.EXTERNAL]: "県外",
};

/**
 * 出品区分から、その区分が持つ県区分の列を返す（設計 §3「系列キーの解決」）。
 * サーバ側の BiddingCalendarService.PrefectureTypesOf と同じ規則で、
 * 画面の列構成・保存ペイロード・取得結果のキーはすべてこの関数から導く。
 *
 * 入札会は県内県外の区別を持たないため 0 の1列、それ以外は県内・県外の2列。
 */
export const prefectureTypesOf = (category: number): number[] =>
  category === ListingCategoryConst.SALE
    ? [PrefTypeConst.NONE]
    : [PrefTypeConst.INTERNAL, PrefTypeConst.EXTERNAL];

/**
 * スケジュール画面のタブに出す出品区分（相対は当システム未取扱のため出さない）
 */
export const ScheduleCategoryTabs = [
  { value: ListingCategoryConst.BIDDING, label: "通常入札" },
  { value: ListingCategoryConst.SALE, label: "入札会" },
] as const;

/**
 * ⚠ **非推奨。新しい画面では使わないこと**（2026-09-17・タスク2）。
 *
 * 🔒 出品区分の選択肢は **`getCategoryOptions(screen, role)` が唯一の出所**（D-0080）。
 *   本配列は出し分けを行わないため、**出してはいけない画面にも入札会が出る**（正確には入札会を含まないので
 *   「出すべき画面に出ない」）。いずれにせよ**規則の外側**にある。
 *
 * 残している理由＝**到達不能なデッドコード `BiddingInputPage`（`/buyer/input`）が唯一の利用者**だから。
 *   当初は削除する判断だったが、デッドコードは P-0040 で存廃を判断するまで触らない方針のため残した
 *   （消すと当該ファイルがビルドエラーになる）。**P-0040 でデッドコードを削除する際に本定数も削除する**。
 */
export const CombinedCategoryOptions = [
  // { value: `${ListingCategoryConst.PRIVATE}_${PrefCategoryConst.INTERNAL}`, label: '相対 - 県内' },
  // { value: `${ListingCategoryConst.PRIVATE}_${PrefCategoryConst.EXTERNAL}`, label: '相対 - 県外' },
  { value: `${ListingCategoryConst.BIDDING}_${PrefTypeConst.INTERNAL}`, label: '通常入札 - 県内扱' },
  { value: `${ListingCategoryConst.BIDDING}_${PrefTypeConst.EXTERNAL}`, label: '通常入札 - 県外扱' },
  // { value: `${ListingCategoryConst.SALE}_${PrefCategoryConst.INTERNAL}`, label: '入札会 - 県内' },
  // { value: `${ListingCategoryConst.SALE}_${PrefCategoryConst.EXTERNAL}`, label: '入札会 - 県外' },
];

/**
 * 【共通ユーティリティ】
 * "2_1" のような文字列を分解して、数値(Number)にして返す
 */
export const parseCombinedCategory = (value: string) => {
  if (!value) return { listingCategory: undefined, prefCategory: undefined };
  const [listingCatStr, prefCatStr] = value.split("_");
  return {
    // Number() を使って数値に変換する
    listingCategory: listingCatStr ? Number(listingCatStr) : undefined,
    prefCategory: prefCatStr ? Number(prefCatStr) : undefined,
  };
};

/**
 * 出品区分の選択欄を持つ画面の識別子（ルート単位・15個）。
 *
 * 🔒 **なぜ画面 ID を必須にするか**（設計 §3・D-0080）:
 *   入札会を出すかどうかは「その画面が誰に開いているか」で決まり、**16画面のうち規則が違うのは4画面だけ**。
 *   呼び出し側が何も書かなくても動く作りにすると、**付け忘れが「静かに壊れる」**（出してはいけない画面に
 *   入札会が出ていても画面は正常に見える）。ID を必須にすることで、新しい画面を足す人が必ず規則の表を見る。
 *
 * ⚠ かんたん入札（`/buyer/input-number`）は**区分の選択欄を持たない**ため ID を与えない（P-0040・No.8）。
 * ⚠ `BiddingInputPage`（`/buyer/input`）は**到達不能なデッドコード**のため ID を与えない（P-0040）。
 */
export const ScreenIdConst = {
  /** 出品情報の一括登録 `/itemBulkUpload` */
  ITEM_BULK_UPLOAD: 'item-bulk-upload',
  /** 出品情報の編集 `/maintenance` */
  ITEM_MAINTENANCE: 'item-maintenance',
  /** 最低落札値の決定 `/bid-min-price-setting` */
  BID_MIN_PRICE_SETTING: 'bid-min-price-setting',
  /** 出品情報の確定/提出/状態確認 `/updatestatus-eachuser`（同一ルートでメニュー名が3通り） */
  ITEM_STATUS_UPDATE: 'item-status-update',
  /** 出品一覧表印刷 `/admin/print-list` */
  REPORT_PRINT_LIST: 'report-print-list',
  /** 手合票印刷 `/admin/print-deal` */
  REPORT_PRINT_DEAL: 'report-print-deal',
  /** 入札Noの手動採番 `/admin/manual-numbering` */
  MANUAL_NUMBERING: 'manual-numbering',
  /** 入札状況確認 `/admin/bidding-monitor` */
  BIDDING_MONITOR: 'bidding-monitor',
  /** 落札確定 `/admin/bid-confirmation` */
  BID_CONFIRMATION: 'bid-confirmation',
  /** 連携用CSV再出力 `/admin/csv-reexport` */
  CSV_REEXPORT: 'csv-reexport',
  /** 落札結果の確認 `/results`（全5ロールが開ける唯一の画面） */
  RESULTS: 'results',
  /** 落札結果一覧表(公開用) `/admin/inquiry/win-list` */
  REPORT_WIN_LIST: 'report-win-list',
  /** 落札結果CSV出力（期間指定） `/admin/result-csv-range` */
  RESULT_CSV_RANGE: 'result-csv-range',
  /** 出品予定の確認 `/listing-schedule`（買手専用） */
  LISTING_SCHEDULE: 'listing-schedule',
  /** 一覧から入札 `/buyer/input2`（買手専用） */
  BUYER_INPUT_LIST: 'buyer-input-list',
} as const;
export type ScreenId = typeof ScreenIdConst[keyof typeof ScreenIdConst];

/**
 * **出品者も使う画面**（全ロールが開けるため、入札会は市場にだけ出す）。
 * 入札会の登録・値決め・確定はすべて市場が行うため（2026-09-17 多々良・D-0080）。
 */
const SELLER_FACING_SCREENS: readonly ScreenId[] = [
  ScreenIdConst.ITEM_BULK_UPLOAD,
  ScreenIdConst.ITEM_MAINTENANCE,
  ScreenIdConst.BID_MIN_PRICE_SETTING,
  ScreenIdConst.ITEM_STATUS_UPDATE,
];

/** 通常入札の2択（どの画面・どのロールでも必ず出る） */
const BIDDING_OPTIONS = [
  { value: `${ListingCategoryConst.BIDDING}_${PrefTypeConst.INTERNAL}`, label: '通常入札 - 県内扱' },
  { value: `${ListingCategoryConst.BIDDING}_${PrefTypeConst.EXTERNAL}`, label: '通常入札 - 県外扱' },
];

/**
 * 入札会の選択肢。
 * 🔒 **1つだけ**——入札会は県内県外の区別を持たないため `"3_0"`（D-0068）。
 *   コメントアウトされていた「入札会 - 県内 / 県外」は**復活させない**。
 */
const SALE_OPTION = {
  value: `${ListingCategoryConst.SALE}_${PrefTypeConst.NONE}`,
  label: '入札会',
};

/**
 * 出品区分のプルダウンに並べる選択肢を返す（設計 §3・D-0080）。
 *
 * ```
 * 入札会を出すか(画面, ロール) =
 *     画面 ∈ {一括登録, 編集, 最低落札値, 確定/提出/状態確認}  →  ロール == 市場(Admin)
 *   : それ以外                                                →  出す
 * ```
 *
 * 🔒 **除外側に条件を書く**のが要点。「市場なら出す」と書くと**買手専用画面で出なくなる**
 *   （買手は Admin ではない）。D-0080 で一度この誤りを記載し訂正した経緯がある。
 *
 * 🔒 **本関数は「プルダウンに何を並べるか」を決めるだけ**——データの絞り込み・権限チェック・
 *   値の検証は行わない。**選択肢を出さないことと、アクセスできないことは別**であり、
 *   URL に直接 `listingCategory=3` を打たれた場合の防御は**サーバの G8**の役目。
 *
 * @param screen 呼び出し元の画面（ルート単位の識別子）
 * @param role ログイン中のロール
 */
export const getCategoryOptions = (screen: ScreenId, role?: UserRole) => {
  // 既知の画面は既定で入札会を出す。出品者も使う4画面だけ市場に限定する。
  if (SELLER_FACING_SCREENS.includes(screen)) {
    return role === UserRole.Admin ? [...BIDDING_OPTIONS, SALE_OPTION] : [...BIDDING_OPTIONS];
  }

  if (Object.values(ScreenIdConst).includes(screen)) {
    return [...BIDDING_OPTIONS, SALE_OPTION];
  }

  // 🔒 未知の画面 ID（型で弾かれるため到達しない想定）は**入札会を含めない側**に倒す。
  //    間違えたときの見え方が「出してはいけない画面に出る（静かに壊れる）」より
  //    「出すべき画面に出ない（使う人がすぐ気づく）」ほうが安全なため。
  return [...BIDDING_OPTIONS];
};
